import { useMemo } from "react";
import type { ValueControlProps } from "@design-system/formControl/formControl.types";
import type { TriggerButtonProps } from "@design-system/overlay/triggerButtonProps";
import { Picker } from "@design-system/Picker/Picker.lib";
import type { PickerItemData } from "@design-system/Picker/Picker.types";

export interface CodeComboboxProps extends ValueControlProps<
    string | null,
    HTMLButtonElement
> {
    buttonSize?: TriggerButtonProps["size"];
}

interface CodeComboboxListProps {
    codes: readonly string[];
    codeLabel: (code: string) => string;
    placeholder: string;
    searchPlaceholder: string;
    emptyText: string;
}

const searchCollator = new Intl.Collator(undefined, {
    usage: "search",
    sensitivity: "base",
    ignorePunctuation: true,
});

/**
 * Substring match that ignores case, accents and punctuation, so "Curacao"
 * finds "Curaçao" (the Base UI Combobox filter these pickers used before).
 */
const matchesCodeLabel = (item: PickerItemData<string>, query: string) => {
    const needle = query.trim();
    for (let start = 0; start <= item.label.length - needle.length; start++) {
        const slice = item.label.slice(start, start + needle.length);
        if (searchCollator.compare(slice, needle) === 0) {
            return true;
        }
    }
    return false;
};

/**
 * Searchable picker over a fixed list of codes (countries, states) that
 * shows each code's display name. Shared by `CountrySelect` and
 * `StateSelect`; `null` means nothing is picked.
 */
export const CodeCombobox = ({
    value,
    onValueChange,
    onBlur,
    name,
    ref,
    invalid,
    disabled,
    readOnly,
    required,
    buttonSize,
    codes,
    codeLabel,
    placeholder,
    searchPlaceholder,
    emptyText,
}: CodeComboboxProps & CodeComboboxListProps) => {
    const items = useMemo(
        () => codes.map((code) => ({ value: code, label: codeLabel(code) })),
        [codes, codeLabel],
    );

    return (
        <Picker.Select<string>
            items={items}
            value={value}
            onValueChange={onValueChange}
            onBlur={onBlur}
            name={name}
            ref={ref}
            aria-invalid={invalid}
            disabled={disabled}
            readOnly={readOnly}
            clearable
            searchable
            searchPlaceholder={searchPlaceholder}
            filter={matchesCodeLabel}
            emptyLabel={emptyText}
            placeholder={placeholder}
            trigger={
                <Picker.Button
                    variant="primary"
                    size={buttonSize ?? "md"}
                    block
                    aria-required={required ? true : undefined}
                />
            }
        />
    );
};
